/*
 * Benefits bar ticker - sections/cvx-hero-trust.liquid
 * Mobile only (<=777px, the theme's breakpoint). Auto-scrolls the row in a seamless loop
 * (the section renders a hidden second copy), pauses while off screen, and stops for good on
 * the first touch, drag or wheel - from then on it is a normal swipeable row, mouse drag included.
 * Does nothing under prefers-reduced-motion.
 */
(function () {
  'use strict';

  var MOBILE = window.matchMedia('(max-width: 777px)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var SPEED = 27; // px per second

  function Ticker(root) {
    this.root = root;
    this.track = root.querySelector('.cvx-hero-trust__track');
    this.pos = 0;
    this.loop = null;
    this.raf = null;
    this.last = 0;
    this.visible = true;
    this.stopped = false;
    this.bind();
    this.sync();
  }

  // PERF: measured once and cached. Reading offsetLeft on every animation frame
  // forced a full layout 60 times a second while the page was still loading
  // (~2.9 s of main-thread work on a mid-range phone, 4 Oct 2026). The width
  // only changes when the row resizes or the web font swaps in, so re-measure
  // then.
  Ticker.prototype.loopWidth = function () {
    if (this.loop == null) {
      var first = this.track.querySelector('.cvx-hero-trust__usp');
      var clone = this.track.querySelector('.cvx-hero-trust__usp.is-clone');
      this.loop = first && clone ? clone.offsetLeft - first.offsetLeft : 0;
    }
    return this.loop;
  };

  Ticker.prototype.remeasure = function () {
    this.loop = null;
    if (this.raf) this.applyLoop();
  };

  // PERF (5 Oct 2026): the row is moved by a CSS animation (.is-ticking in
  // cvx-hero-trust.css), which the browser runs off the main thread. Moving it
  // from JavaScript on every frame cost ~3.9 s of main-thread time on
  // PageSpeed's phone while the page loaded. The loop width and duration are
  // handed over as CSS variables; speed is unchanged (SPEED px per second).
  Ticker.prototype.applyLoop = function () {
    var loop = this.loopWidth();
    this.root.style.setProperty('--cvx-ht-loop', loop + 'px');
    this.root.style.setProperty('--cvx-ht-dur', (loop > 0 ? loop / SPEED : 0) + 's');
  };

  Ticker.prototype.canRun = function () {
    return MOBILE.matches && !REDUCED.matches && !this.stopped;
  };

  Ticker.prototype.sync = function () {
    if (this.canRun() && this.visible) this.start();
    else this.pause();
    if (!MOBILE.matches) this.reset();
  };

  // this.raf now just means "running" (kept so the rest reads as before).
  Ticker.prototype.start = function () {
    if (this.raf) return;
    this.raf = true;
    this.applyLoop();
    this.root.classList.add('is-ticking');
    this.root.classList.remove('is-paused');
  };

  Ticker.prototype.pause = function () {
    if (!this.raf) return;
    this.raf = null;
    this.root.classList.add('is-paused');
  };

  // Where the animation has got to, in px.
  Ticker.prototype.current = function () {
    var m = window.getComputedStyle(this.track).transform;
    if (!m || m === 'none') return 0;
    var v = m.match(/matrix(3d)?\(([^)]+)\)/);
    if (!v) return 0;
    var n = v[2].split(',');
    return -parseFloat(n[v[1] ? 12 : 4]) || 0;
  };

  Ticker.prototype.reset = function () {
    this.raf = null;
    this.pos = 0;
    this.root.classList.remove('is-ticking', 'is-paused');
    this.track.style.transform = '';
  };

  // First interaction: freeze where it is and hand over to native scrolling at the same spot
  Ticker.prototype.stop = function () {
    if (this.stopped) return;
    this.stopped = true;
    var pos = this.current();
    this.reset();
    this.root.classList.add('is-manual');
    this.root.scrollLeft = pos;
  };

  Ticker.prototype.bind = function () {
    var self = this;
    var root = this.root;
    var drag = null;

    root.addEventListener('touchstart', function () { self.stop(); }, { passive: true });
    root.addEventListener('wheel', function () { self.stop(); }, { passive: true });

    root.addEventListener('pointerdown', function (e) {
      if (!MOBILE.matches) return;
      self.stop();
      if (e.pointerType !== 'mouse') return;
      drag = { x: e.clientX, left: root.scrollLeft };
      root.classList.add('is-dragging');
      root.setPointerCapture(e.pointerId);
    });
    root.addEventListener('pointermove', function (e) {
      if (drag) root.scrollLeft = drag.left - (e.clientX - drag.x);
    });
    function endDrag() {
      drag = null;
      root.classList.remove('is-dragging');
    }
    root.addEventListener('pointerup', endDrag);
    root.addEventListener('pointercancel', endDrag);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
        self.sync();
      }).observe(root);
    }

    if ('ResizeObserver' in window) {
      new ResizeObserver(function () { self.remeasure(); }).observe(this.track);
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { self.remeasure(); });
    }

    var onChange = function () { self.remeasure(); self.sync(); };
    if (MOBILE.addEventListener) {
      MOBILE.addEventListener('change', onChange);
      REDUCED.addEventListener('change', onChange);
    }
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-cvx-hero-ticker]').forEach(function (el) {
      if (el.cvxTicker) return;
      el.cvxTicker = new Ticker(el);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  // Theme editor re-renders the section in place
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
